import Button from "@/Components/Button/Button";
import Error from "@/Components/Error";
import Icon from "@/Components/Icon/Icon";
import AiImageBadge from "@/Components/ImageLibrary/AiImageBadge";
import ImageLibraryEmptyState from "@/Components/ImageLibrary/ImageLibraryEmptyState";
import TagChip from "@/Components/ImageLibrary/TagChip";
import ImageWithFallback from "@/Components/ImageWithFallback";
import Search from "@/Components/Input/Search";
import Select from "@/Components/Select/Select";
import useDebounce from "@/shared/hooks/useDebounce";
import useIsAdmin from "@/shared/hooks/useIsAdmin";
import { isAiImage } from "@/shared/imageLibrary/aiImage";
import { ImageLibraryItem, ImageLibraryParams } from "@/shared/imageLibrary/types";
import useFranchiseQuery from "@/shared/queries/franchise/useFranchiseQuery";
import useImageLibraryQuery from "@/shared/queries/imageLibrary/useImageLibraryQuery";
import { KitchenTypeEnum } from "@/shared/utils/interfaces";
import { getKitchenTypeLabel, KitchenTypeOption, kitchenTypeOptions } from "@/shared/utils/kitchenTypes";
import { cn } from "@/shared/utils/cn";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { matchesLibrarySearch, suggestSearches } from "./librarySuggestions";

const gridClassName = "m-0 grid grid-cols-2 gap-4 p-0 sm:grid-cols-3 md:grid-cols-4";

interface LibraryImagePickerProps {
  params: ImageLibraryParams;
  /** Text the suggestions are based on, e.g. the product name. */
  suggestFor?: string;
  selected: ImageLibraryItem[];
  /** Click on an image. */
  onSelect: (image: ImageLibraryItem) => void;
  /** Double click / Enter on a selected image. */
  onConfirm?: (image: ImageLibraryItem) => void;
  /** Images that aren't selected can't be picked once this many are selected. */
  maxSelected?: number;
  disabled?: boolean;
}

const LibraryImagePicker = ({
  params: queryParams,
  suggestFor = "",
  selected,
  onSelect,
  onConfirm,
  maxSelected = Infinity,
  disabled = false,
}: LibraryImagePickerProps) => {
  const { t } = useTranslation();
  // Users allowed to read the franchise start with its kitchen types selected.
  const canReadFranchise = useIsAdmin();
  const franchiseQuery = useFranchiseQuery(canReadFranchise);
  // null until changed, so the franchise kitchen types are used by default.
  const [kitchenTypes, setKitchenTypes] = useState<KitchenTypeEnum[] | null>(null);
  const selectedKitchenTypes = useMemo<KitchenTypeEnum[]>(
    () => kitchenTypes ?? franchiseQuery.data?.kitchen_types ?? [],
    [kitchenTypes, franchiseQuery.data]
  );

  // The endpoint has no search and takes only one kitchen type, so all images are loaded once and filtered here.
  const imagesQuery = useImageLibraryQuery(
    queryParams,
    // Wait for the franchise so the first results are already filtered on its kitchen types.
    !canReadFranchise || !franchiseQuery.isPending
  );
  const kitchenItems = useMemo(
    () =>
      (imagesQuery.data ?? []).filter(
        (item) =>
          !selectedKitchenTypes.length || (item.kitchen_types ?? []).some((type) => selectedKitchenTypes.includes(type))
      ),
    [imagesQuery.data, selectedKitchenTypes]
  );
  // null until prefilled from the product name, so we don't flash all images first.
  const [search, setSearch] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 250);

  const suggestionChips = useMemo(
    () => suggestSearches(suggestFor, kitchenItems),
    [suggestFor, kitchenItems]
  );

  useEffect(() => {
    if (search !== null || !imagesQuery.isSuccess) return;
    // Suggestions always have results; otherwise start with all images.
    setSearch(suggestionChips[0] ?? "");
  }, [search, suggestionChips, imagesQuery.isSuccess]);

  // Ready once prefilled and debounced, so the grid doesn't switch results right after loading.
  const isReady = search !== null && debouncedSearch === search;
  const params = { search: (debouncedSearch ?? "").trim() };
  const items = useMemo(
    () => kitchenItems.filter((item) => matchesLibrarySearch(item, params.search)),
    [kitchenItems, params.search]
  );

  const activeSearch = (search ?? "").trim().toLowerCase();
  const isFull = selected.length >= maxSelected;

  const renderGrid = () => {
    if (imagesQuery.isError) {
      return (
        <div className="py-12">
          <Error refetch={imagesQuery.refetch} message={t("imageLibrary.errors.load")} />
        </div>
      );
    }

    if (!isReady || imagesQuery.isPending) {
      return (
        <ul className={gridClassName} aria-hidden="true">
          {Array.from({ length: 8 }, (_, index) => (
            <li key={index} className="list-none">
              <Skeleton className="block aspect-square" style={{ lineHeight: "unset" }} />
              <Skeleton width="70%" height="1.4rem" className="mt-2" />
            </li>
          ))}
        </ul>
      );
    }

    if (imagesQuery.data.length === 0) {
      return (
        <ImageLibraryEmptyState
          icon="download"
          title={t("imageLibrary.empty.title")}
          description={t("productImage.library.emptyDescription")}
        />
      );
    }

    if (items.length === 0) {
      return (
        <ImageLibraryEmptyState
          title={t("imageLibrary.empty.noResultsFor", { search: params.search })}
          description={t("productImage.library.noResultsDescription")}
          action={
            <Button
              variant="secondary"
              onClick={() => {
                setSearch("");
                setKitchenTypes([]);
              }}
            >
              {t("productImage.library.showAll")}
            </Button>
          }
        />
      );
    }

    return (
      <ul
        className={gridClassName}
        aria-label={t("productImage.library.results")}
      >
        {items.map((image) => {
          const isSelected = selected.some((item) => item.item_id === image.item_id);
          return (
            <li key={image.item_id} className="list-none">
              <button
                type="button"
                disabled={disabled || (isFull && !isSelected)}
                aria-pressed={isSelected}
                title={image.description}
                onClick={() => onSelect(image)}
                onDoubleClick={onConfirm && (() => onConfirm(image))}
                onKeyDown={(e) => {
                  if (onConfirm && e.key === "Enter" && isSelected) {
                    e.preventDefault();
                    onConfirm(image);
                  }
                }}
                className="group flex w-full cursor-pointer flex-col gap-2 border-none bg-transparent p-0 text-left disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none"
              >
                <div
                  className={cn(
                    "relative aspect-square w-full overflow-hidden rounded-[0.8rem] bg-light transition-shadow",
                    isSelected
                      ? "shadow-[0_0_0_0.3rem_var(--primary)]"
                      : "shadow-[0_0_0_0.1rem_var(--border)] group-hover:shadow-[0_0_0_0.2rem_var(--primary)] group-focus-visible:shadow-[0_0_0_0.3rem_var(--primary)]"
                  )}
                >
                  <ImageWithFallback
                    src={image.url}
                    loading="lazy"
                    style={{ width: "100%", height: "100%", marginTop: 0, borderRadius: 0, objectFit: "cover" }}
                  />
                  {isAiImage(image.cloudinary_id) && <AiImageBadge className="bottom-2 left-2" />}
                  {isSelected && (
                    <span className="absolute right-2 top-2 flex h-[2.6rem] w-[2.6rem] items-center justify-center rounded-full bg-primary shadow-md">
                      <Icon className="h-[1.2rem] w-[1.2rem] fill-white" variants="check" />
                    </span>
                  )}
                </div>
                <span className={cn("truncate text-btn-small", isSelected ? "font-semibold text-primary" : "text-text")}>
                  {image.name}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <Search
        value={search ?? ""}
        onChange={(e) => setSearch(e.target.value)}
        onDeleteClick={() => setSearch("")}
        placeholder={t("imageLibrary.searchPlaceholder")}
        aria-label={t("imageLibrary.searchPlaceholder")}
        autoFocus
        inputLeftAddon={<Icon className="h-[1.4rem] w-[1.4rem] fill-text-secondary" variants="search" />}
      />

      <Select
        isMulti
        placeholder={t("franchise.branch.kitchenType")}
        aria-label={t("franchise.branch.kitchenType")}
        options={kitchenTypeOptions}
        value={kitchenTypeOptions.filter((option) => selectedKitchenTypes.includes(option.value))}
        getOptionValue={(option) => option.value}
        getOptionLabel={(option) => getKitchenTypeLabel(t, option)}
        onChange={(options: KitchenTypeOption[]) => setKitchenTypes((options ?? []).map((option) => option.value))}
        isDisabled={disabled}
      />

      {suggestionChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-btn-small font-semibold text-text-secondary">
            {t("productImage.library.suggestions")}
          </span>
          {suggestionChips.map((name) => (
            <TagChip
              key={name}
              name={name}
              active={activeSearch === name.toLowerCase()}
              onClick={() => setSearch(name)}
            />
          ))}
          <TagChip
            name={t("productImage.library.allImages")}
            active={isReady && activeSearch === ""}
            onClick={() => setSearch("")}
          />
        </div>
      )}

      <div className="min-h-[30rem]">{renderGrid()}</div>
    </div>
  );
};

export default LibraryImagePicker;
